import {observer} from 'mobx-react';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PromiseMessage } from '../../../lib/components';
import { ReactNodeGenerator } from '../../../lib/helpers';
import { TeacherStudentsSceneStore, Lesson } from './TeacherStudentsSceneStore';
import { StudentDetailsResponse, StudentDetailExamItem, StudentDetailEssayItem, ExaminationLessonItem } from '../models/classroom-models';
import { ExamLoaders } from '../../exam/ExamLoaders';
import { TeacherExamResultResponse } from '../../exam/ExamModels';
import { Alert, Badge, Card, CardBody, CardHeader } from 'reactstrap';
import { X5LessonSnapshot } from '../../../domain';

const GRADABLE_CONTENT_TYPES = ['matching-question', 'select-question', 'gap-filling', 'multiple-choice-question', 'grouping-exercise'];

function calculateSnapshotStats(snapshot: X5LessonSnapshot | Record<string, unknown> | null): { maxBal: number; totalQuestions: number } {
  if (!snapshot) return { maxBal: 0, totalQuestions: 0 };
  const pages = (snapshot as Record<string, unknown>).PageSnapshots ?? (snapshot as Record<string, unknown>).pageSnapshots;
  if (!Array.isArray(pages)) return { maxBal: 0, totalQuestions: 0 };
  let maxBal = 0;
  let totalQuestions = 0;
  for (const page of pages) {
    const p = page as Record<string, unknown>;
    const pushableBlocks = p.PushableBlockSnapshots ?? p.pushableBlockSnapshots;
    if (!Array.isArray(pushableBlocks)) continue;
    for (const pb of pushableBlocks) {
      const pbRecord = pb as Record<string, unknown>;
      const blocks = pbRecord.BlockSnapshots ?? pbRecord.blockSnapshots;
      if (!Array.isArray(blocks)) continue;
      for (const block of blocks) {
        const b = block as Record<string, unknown>;
        const bal = b.Bal ?? b.bal;
        if (typeof bal === 'number') maxBal += bal;
        const contents = b.ContentSnapshots ?? b.contentSnapshots;
        if (!Array.isArray(contents)) continue;
        const gradable = contents.filter((c: Record<string, unknown>) => {
          const type = c.ContentType ?? c.contentType;
          return typeof type === 'string' && GRADABLE_CONTENT_TYPES.includes(type);
        });
        totalQuestions += gradable.length;
      }
    }
  }
  return { maxBal, totalQuestions };
}

const HomeworkModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  lessons: Lesson[];
  selectedLesson: Lesson | null;
  onLessonSelect: (lesson: Lesson) => void;
  onSend: () => void;
  studentName?: string;
}> = ({ isOpen, onClose, lessons, selectedLesson, onLessonSelect, onSend, studentName }) => {
  if (!isOpen) return null;

  return (
    <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Send homework {studentName && `for ${studentName}`}
            </h5>
            <i className='fas fa-times' style={{cursor: 'pointer', fontSize: '1.2rem'}} onClick={onClose}></i>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">Choose lesson:</label>
              {
                lessons.length === 0 && (
                  <div className="alert alert-info">
                    No lessons available.
                  </div>
                )
              }
              <div className="list-group" style={{height: 231, overflow: 'auto'}}>
                {lessons.map((lesson) => (
                  <button
                    key={lesson.id}
                    type="button"
                    className={`list-group-item list-group-item-action ${
                      selectedLesson?.id === lesson.id ? 'active' : ''
                    }`}
                    onClick={() => onLessonSelect(lesson)}
                  >
                    <div className="d-flex w-100 justify-content-between">
                      <h6 className="mb-1">{lesson.title}</h6>
                      <small>{lesson.date.toLocaleDateString()}</small>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSend}
              disabled={!selectedLesson}
            >
              Send Homework
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TeacherExamResultModal: React.FC<{
  examId: number | null;
  onClose: () => void;
}> = ({ examId, onClose }) => {
  const { t } = useTranslation();
  const [result, setResult] = useState<TeacherExamResultResponse | null>(null);
  const [snapshot, setSnapshot] = useState<X5LessonSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (examId == null) {
      setResult(null);
      setSnapshot(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      ExamLoaders.teacherGetExamResult(examId),
      ExamLoaders.teacherGetExamLessonSnapshot(examId)
    ])
      .then(([resultData, snapshotData]) => {
        if (!cancelled) {
          setResult(resultData);
          setSnapshot(snapshotData);
        }
      })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load result'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [examId]);

  if (examId == null) return null;

  const getContentTypeName = (type: string): string =>
    t(`exam.content-type.${type}`, type);

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" style={{ maxHeight: '90vh' }}>
        <div className="modal-content" style={{ maxHeight: '90vh' }}>
          <div className="modal-header">
            <h5 className="modal-title">Exam result — Details</h5>
            <i className="fas fa-times" style={{ cursor: 'pointer', fontSize: '1.2rem' }} onClick={onClose} aria-hidden />
          </div>
          <div className="modal-body" style={{ overflowY: 'auto' }}>
            {loading && <p className="text-muted">Loading...</p>}
            {error && <Alert color="danger">{error}</Alert>}
            {result && !loading && (
              <Card className="mb-0">
                <CardHeader className="bg-primary text-white">
                  <h4 className="mb-0">{t('exam.results.title')}</h4>
                </CardHeader>
                <CardBody>
                  {(() => {
                    const r = result as TeacherExamResultResponse & { totalBal?: number; mistakesJson?: string };
                    const totalBal = r.TotalBal ?? r.totalBal ?? 0;
                    const mistakesJson = r.MistakesJson ?? r.mistakesJson ?? '';
                    let mistakes: Array<{ studentAnswer: string; correctAnswer: string; contentType: string; studentAnswerParts?: Array<{ text: string; isCorrect: boolean }> }> = [];
                    try {
                      mistakes = mistakesJson ? JSON.parse(mistakesJson) : [];
                    } catch {
                      mistakes = [];
                    }
                    const { maxBal, totalQuestions } = calculateSnapshotStats(snapshot);
                    const incorrectCount = mistakes.length;
                    const correctCount = Math.max(0, totalQuestions - incorrectCount);
                    const percentage = maxBal > 0 ? Math.round((totalBal / maxBal) * 100) : 0;
                    const hasNoQuestions = totalQuestions === 0 && maxBal === 0;
                    const hasNoMistakes = mistakes.length === 0 && !hasNoQuestions;
                    return (
                      <>
                        <div className="text-center mb-4">
                          <h2>
                            <Badge
                              color={hasNoQuestions ? 'secondary' : percentage >= 70 ? 'success' : percentage >= 50 ? 'warning' : 'danger'}
                              style={{ fontSize: '2rem', padding: '15px 30px' }}
                            >
                              {t('exam.results.score', { score: totalBal, max: maxBal, percent: percentage })}
                            </Badge>
                          </h2>
                          {totalQuestions > 0 && (
                            <div className="mt-3" style={{ fontSize: '1.1rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem 1.5rem', justifyContent: 'center' }}>
                              <span>{t('exam.results.total-questions')}: <strong>{totalQuestions}</strong></span>
                              <span className="text-success">{t('exam.results.correct')}: <strong>{correctCount}</strong></span>
                              <span className="text-danger">{t('exam.results.incorrect')}: <strong>{incorrectCount}</strong></span>
                            </div>
                          )}
                        </div>
                        {hasNoQuestions && (
                          <Alert color="warning" className="text-center">{t('exam.results.no-answers')}</Alert>
                        )}
                        {mistakes.length > 0 && (
                          <div>
                            <h5 className="text-danger mb-3">{t('exam.results.mistakes')} ({mistakes.length}):</h5>
                            {mistakes.map((mistake: any, index: number) => {
                              const yourAnswer = mistake.studentAnswer || t('exam.results.no-answer');
                              const isMatching = mistake.contentType === 'matching-question';
                              const blockStyle = isMatching ? { whiteSpace: 'pre-line' as const } : undefined;
                              const hasParts = mistake.studentAnswerParts && mistake.studentAnswerParts.length > 0;
                              return (
                                <Alert key={index} color="danger" className="mb-2">
                                  <div style={{ marginBottom: '0.35rem' }}>
                                    <strong style={{ color: '#6f42c1' }}>{t('exam.results.type')}:</strong>{' '}
                                    <span style={{ color: '#495057' }}>{getContentTypeName(mistake.contentType)}</span>
                                  </div>
                                  <div style={{ marginBottom: '0.35rem' }}>
                                    <strong style={{ color: '#0d6efd' }}>{t('exam.results.your-answer')}:</strong>{' '}
                                    {hasParts ? (
                                      <span style={blockStyle}>
                                        {mistake.studentAnswerParts.map((part: { text: string; isCorrect: boolean }, i: number) => (
                                          <React.Fragment key={i}>
                                            {i > 0 && (isMatching ? <br /> : ', ')}
                                            <span style={{ color: part.isCorrect ? '#198754' : '#495057' }}>{part.text}</span>
                                          </React.Fragment>
                                        ))}
                                      </span>
                                    ) : (
                                      <span style={{ color: '#495057', ...blockStyle }}>{yourAnswer}</span>
                                    )}
                                  </div>
                                  <div>
                                    <strong style={{ color: '#d63384' }}>{t('exam.results.correct-answer')}:</strong>{' '}
                                    <span style={{ color: '#495057', ...blockStyle }}>{mistake.correctAnswer}</span>
                                  </div>
                                </Alert>
                              );
                            })}
                          </div>
                        )}
                        {hasNoMistakes && (
                          <Alert color="success" className="text-center">{t('exam.results.no-mistakes')}</Alert>
                        )}
                      </>
                    );
                  })()}
                </CardBody>
              </Card>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Student Details Modal
const StudentDetailsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  studentDetails: StudentDetailsResponse | null;
  isLoading: boolean;
  onExamDetailsClick?: (examId: number) => void;
}> = ({ isOpen, onClose, studentDetails, isLoading, onExamDetailsClick }) => {
  if (!isOpen) return null;

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'New': return 'badge bg-primary';
      case 'InProgress': return 'badge bg-warning text-dark';
      case 'Done': return 'badge bg-info';
      case 'Accepted': return 'badge bg-success';
      case 'Rejected': return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  };

  return (
    <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{maxHeight: '90vh'}}>
        <div className="modal-content" style={{maxHeight: '90vh'}}>
          <div className="modal-header">
            <h5 className="modal-title">
              {studentDetails ? `${studentDetails.FirstName} ${studentDetails.LastName}` : 'Student Details'}
            </h5>
            <i className='fas fa-times' style={{cursor: 'pointer', fontSize: '1.2rem'}} onClick={onClose}></i>
          </div>
          <div className="modal-body" style={{overflowY: 'auto'}}>
            {isLoading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status"></div>
              </div>
            )}
            
            {!isLoading && studentDetails && (
              <>
                {/* Student Info */}
                <div className="card mb-3">
                  <div className="card-header">
                    <strong>Student Information</strong>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p><strong>Level:</strong> {studentDetails.LevelName || 'Not set'}</p>
                        <p><strong>Age Category:</strong> {studentDetails.AgeCategoryName || 'Not set'}</p>
                        <p><strong>Language:</strong> {studentDetails.LanguageName || 'Not set'}</p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Time Zone:</strong> {studentDetails.TimeZoneName || 'Not set'}</p>
                        <p><strong>Balance:</strong> {studentDetails.Balance}</p>
                        <p><strong>Completed Lessons:</strong> {studentDetails.CompletedLessons}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Exams */}
                <div className="card mb-3">
                  <div className="card-header">
                    <strong>Exam Results</strong>
                  </div>
                  <div className="card-body">
                    {studentDetails.Exams.length === 0 ? (
                      <p className="text-muted">No exams yet</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm table-hover mb-0">
                          <thead>
                            <tr>
                              <th>Unit</th>
                              <th>Lesson</th>
                              <th>Status</th>
                              <th>Score</th>
                              <th>Date</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {studentDetails.Exams.map((exam: StudentDetailExamItem) => (
                              <tr key={exam.ExamId}>
                                <td>{exam.UnitName}</td>
                                <td>{exam.LessonName}</td>
                                <td><span className={getStatusBadgeClass(exam.Status)}>{exam.Status}</span></td>
                                <td>{exam.Bal !== null ? exam.Bal : '-'}</td>
                                <td>{new Date(exam.CreatedAt).toLocaleDateString()}</td>
                                <td>
                                  {(exam.Status === 'Accepted' || exam.Status === 'Rejected') && onExamDetailsClick && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => onExamDetailsClick(exam.ExamId)}
                                    >
                                      Details
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>

                {/* Essays */}
                <div className="card">
                  <div className="card-header">
                    <strong>Essays</strong>
                  </div>
                  <div className="card-body">
                    {studentDetails.Essays.length === 0 ? (
                      <p className="text-muted">No essays yet</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm table-hover mb-0">
                          <thead>
                            <tr>
                              <th>Title</th>
                              <th>Status</th>
                              <th>Rating</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {studentDetails.Essays.map((essay: StudentDetailEssayItem) => (
                              <tr key={essay.EssayId}>
                                <td>{essay.Title}</td>
                                <td><span className={getStatusBadgeClass(essay.Status)}>{essay.Status}</span></td>
                                <td>{essay.Rating !== null ? `${essay.Rating}/5` : '-'}</td>
                                <td>{new Date(essay.CreatedAt).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Exam Modal
const ExamModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  lessons: ExaminationLessonItem[];
  selectedLesson: ExaminationLessonItem | null;
  onLessonSelect: (lesson: ExaminationLessonItem) => void;
  onSend: () => void;
  studentName?: string;
  isLoading: boolean;
  timeLimitMinutes: number | null;
  onTimeLimitChange: (value: number | null) => void;
}> = ({ isOpen, onClose, lessons, selectedLesson, onLessonSelect, onSend, studentName, isLoading, timeLimitMinutes, onTimeLimitChange }) => {
  if (!isOpen) return null;

  return (
    <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Send Exam {studentName && `to ${studentName}`}
            </h5>
            <i className='fas fa-times' style={{cursor: 'pointer', fontSize: '1.2rem'}} onClick={onClose}></i>
          </div>
          <div className="modal-body">
            {isLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status"></div>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <label className="form-label">Choose examination lesson:</label>
                  {lessons.length === 0 ? (
                    <div className="alert alert-info">
                      No examination lessons available for this student's level.
                    </div>
                  ) : (
                    <div className="list-group" style={{maxHeight: 250, overflow: 'auto'}}>
                      {lessons.map((lesson) => (
                        <button
                          key={lesson.LessonId}
                          type="button"
                          className={`list-group-item list-group-item-action ${
                            selectedLesson?.LessonId === lesson.LessonId ? 'active' : ''
                          }`}
                          onClick={() => onLessonSelect(lesson)}
                        >
                          <div className="d-flex w-100 justify-content-between">
                            <h6 className="mb-1">{lesson.LessonName}</h6>
                          </div>
                          <small className={selectedLesson?.LessonId === lesson.LessonId ? 'text-light' : 'text-muted'}>
                            Unit: {lesson.UnitName}
                          </small>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Time Limit (minutes, optional):</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="e.g. 60"
                    min="1"
                    value={timeLimitMinutes ?? ''}
                    onChange={(e) => onTimeLimitChange(e.target.value ? parseInt(e.target.value) : null)}
                  />
                  <small className="text-muted">Leave empty for no time limit</small>
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSend}
              disabled={!selectedLesson || isLoading}
            >
              Send Exam
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

function* renderer(store: TeacherStudentsSceneStore): ReactNodeGenerator {
    
    const resultState = store.resultState;
  
    yield (<PromiseMessage key="search-result-status"
                           label="Loading result" state={resultState} align="right"/>);
  
    if (resultState?.isResolved !== true)
      return;
  
    const response = resultState.value;   
  
    if (response === null || response.length === 0) {
      yield (
        <div className="row" key="no-results">
          <div className="col text-center">
            <div className="alert alert-info">
              No records
            </div>
          </div>
        </div>);
      return;
    }
    const rows = response;
  
    yield (
      <div className="row" key="results-table">
        <div className="col">
          <table className="table table-sm table-bordered table-striped table-hover">
            <thead>
            <tr>
              <th><b>Last name</b></th>
              <th><b>First name</b></th>
              <th><b>TimeZone</b></th>
              <th><b>Level</b></th>
              <th><b>Actions</b></th>
            </tr>
            </thead>
            <tbody>
            {
              rows.map((user, index) => (
                <tr key={index}>
                  <td>{user.LastName}</td>
                  <td>
                    <a 
                      href="#" 
                      onClick={(e) => { e.preventDefault(); store.openStudentDetailsModal(user); }}
                      style={{cursor: 'pointer', textDecoration: 'underline'}}
                    >
                      {user.FirstName}
                    </a>
                  </td>
                  <td>{user.TimeZone?.UtcName}</td>
                  <td>{user.Student?.Level?.Name}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm me-1"
                      onClick={() => store.openHomeworkModal(user)}
                    >
                      Send HW
                    </button>
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => store.openExamModal(user)}
                    >
                      Send Exam
                    </button>
                  </td>
                </tr>
              ))
            }
            </tbody>
          </table>
        </div>
      </div>
    );

    // Homework Modal
    yield (
      <HomeworkModal
        key="homework-modal"
        isOpen={store.isHomeworkModalOpen}
        onClose={store.closeHomeworkModal}
        lessons={store.lessons}
        selectedLesson={store.selectedLesson}
        onLessonSelect={store.setSelectedLesson}
        onSend={store.sendHomework}
        studentName={store.selectedStudent ? `${store.selectedStudent.FirstName} ${store.selectedStudent.LastName}` : undefined}
      />
    );

    // Student Details Modal
    yield (
      <StudentDetailsModal
        key="student-details-modal"
        isOpen={store.isStudentDetailsModalOpen}
        onClose={store.closeStudentDetailsModal}
        studentDetails={store.studentDetails}
        isLoading={store.isLoadingStudentDetails}
        onExamDetailsClick={(examId) => store.setSelectedExamIdForResult(examId)}
      />
    );

    // Teacher exam result details modal
    yield (
      <TeacherExamResultModal
        key="teacher-exam-result-modal"
        examId={store.selectedExamIdForResult}
        onClose={() => store.setSelectedExamIdForResult(null)}
      />
    );

    // Exam Modal
    yield (
      <ExamModal
        key="exam-modal"
        isOpen={store.isExamModalOpen}
        onClose={store.closeExamModal}
        lessons={store.examinationLessons}
        selectedLesson={store.selectedExaminationLesson}
        onLessonSelect={store.setSelectedExaminationLesson}
        onSend={store.sendExam}
        studentName={store.selectedStudent ? `${store.selectedStudent.FirstName} ${store.selectedStudent.LastName}` : undefined}
        isLoading={store.isLoadingExaminationLessons}
        timeLimitMinutes={store.examTimeLimitMinutes}
        onTimeLimitChange={store.setExamTimeLimitMinutes}
      />
    );
  }

@observer
export class TeacherStudentsScene extends React.Component {
  store: TeacherStudentsSceneStore;

  constructor(props: any) {
    super(props);
    this.store = new TeacherStudentsSceneStore();
  }

  render() {
    return (
        <div>
          <div className="row mb-3" key="search-input">
          <div className="col">
            <div className="input-group w-25">
              <input
                type="text"
                className="form-control"
                placeholder="Search by first or last name..."
                value={this.store.searchQuery}
                onChange={(e) => this.store.setSearchQuery(e.target.value)}
              />
            </div>
            {this.store.searchQuery.length === 1 && (
              <small className="text-muted">
                Enter at least 2 characters to search
              </small>
            )}
          </div>
        </div>

        {Array.from(renderer(this.store))}
      </div>
    );
  }

  componentWillUnmount() {
    this.store.dispose();
  }
}
